import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Topbar } from '@/components/Topbar';
import { Pagination } from '@/components/Pagination';
import { subscriptionsService, SubscriptionRow } from '@/services/subscriptions.service';
import { CalendarClock, Pause, Play, Repeat, Sparkles, TimerOff } from 'lucide-react';

interface Page { items: SubscriptionRow[]; total: number; page: number; limit: number; }

/** How each lifecycle event reads and looks on the rail. */
const ACTION: Record<string, { verb: string; icon: React.ReactNode; bg: string; text: string }> = {
  created:     { verb: 'signed up on',        icon: <Sparkles size={13} />,      bg: 'bg-success/15',    text: 'text-success' },
  reactivated: { verb: 'was reactivated on',  icon: <Play size={13} />,          bg: 'bg-success/15',    text: 'text-success' },
  changed:     { verb: 'moved to',            icon: <Repeat size={13} />,        bg: 'bg-info/15',       text: 'text-info' },
  extended:    { verb: 'had expiry extended', icon: <CalendarClock size={13} />, bg: 'bg-primary/15',    text: 'text-primary' },
  suspended:   { verb: 'was suspended on',    icon: <Pause size={13} />,         bg: 'bg-danger/15',     text: 'text-danger' },
  expired:     { verb: 'expired on',          icon: <TimerOff size={13} />,      bg: 'bg-surface-alt',   text: 'text-text-muted' },
};

function dayLabel(iso: string): string {
  const d = new Date(iso);
  const today = new Date();
  const startOf = (x: Date) => new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime();
  const diffDays = Math.round((startOf(today) - startOf(d)) / 864e5);
  if (diffDays === 0) return 'Today';
  if (diffDays === 1) return 'Yesterday';
  return d.toLocaleDateString(undefined, { day: 'numeric', month: 'long', year: 'numeric' });
}

/**
 * Every plan change, suspension and renewal across the book of business.
 * Grouped by day on a single rail — this is a chronological ledger, so the
 * timeline treatment carries real information rather than decoration.
 */
export default function SubscriptionsList() {
  const [data, setData] = useState<Page | null>(null);
  const [page, setPage] = useState(1);
  useEffect(() => { subscriptionsService.listAll(page, 30).then(setData); }, [page]);

  const groups = useMemo(() => {
    const out: { day: string; rows: SubscriptionRow[] }[] = [];
    for (const r of data?.items ?? []) {
      const day = dayLabel(r.createdAt);
      const last = out[out.length - 1];
      if (last && last.day === day) last.rows.push(r);
      else out.push({ day, rows: [r] });
    }
    return out;
  }, [data]);

  return (
    <>
      <Topbar
        title="Subscriptions"
        breadcrumb="Customers / Subscriptions"
        right={data ? <span className="text-xs text-text-muted">{data.total} events</span> : undefined}
      />
      <div className="space-y-4 p-6">
        {!data ? (
          <div className="space-y-2">
            {[0, 1, 2, 3].map((i) => <div key={i} className="h-14 animate-pulse rounded-xl bg-surface-alt/60" />)}
          </div>
        ) : groups.length === 0 ? (
          <div className="rounded-xl border border-dashed border-border p-10 text-center">
            <p className="text-sm font-medium text-text-primary">No subscription events yet</p>
            <p className="mt-1 text-xs text-text-muted">
              Plan changes, suspensions and renewals will appear here as you make them.
            </p>
          </div>
        ) : (
          <div className="rounded-xl border border-border bg-surface">
            {groups.map((g, gi) => (
              <section key={g.day} className={gi > 0 ? 'border-t border-border' : undefined}>
                <h2 className="px-5 pb-1 pt-4 text-[11px] font-semibold text-text-muted">{g.day}</h2>
                <ul className="px-5 pb-3">
                  {g.rows.map((r, i) => {
                    const meta = ACTION[r.action] ?? { verb: r.action, icon: <Repeat size={13} />, bg: 'bg-surface-alt', text: 'text-text-muted' };
                    const last = i === g.rows.length - 1;
                    return (
                      <li key={r._id} className="relative flex gap-3 pb-3 pl-1">
                        {/* rail */}
                        {!last && <span className="absolute left-[17px] top-7 h-full w-px bg-border" aria-hidden />}
                        <span className={`relative z-10 mt-1 flex h-7 w-7 shrink-0 items-center justify-center rounded-full ring-4 ring-surface ${meta.bg} ${meta.text}`}>
                          {meta.icon}
                        </span>
                        <div className="min-w-0 flex-1 pt-0.5">
                          <p className="text-[13px] leading-5 text-text-primary">
                            <Link to={`/companies/${r.companyUid}`} className="font-medium hover:text-primary">
                              {r.company?.name ?? r.companyUid}
                            </Link>
                            <span className="text-text-muted"> {meta.verb} </span>
                            <span className="font-medium">{r.plan?.displayName ?? '—'}</span>
                          </p>
                          {r.note && <p className="mt-0.5 text-[11.5px] leading-4 text-text-muted">{r.note}</p>}
                        </div>
                        <time className="shrink-0 pt-1 text-[11px] tabular-nums text-text-muted" dateTime={r.createdAt}>
                          {new Date(r.createdAt).toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })}
                        </time>
                      </li>
                    );
                  })}
                </ul>
              </section>
            ))}
          </div>
        )}
        {data && <Pagination page={data.page} total={data.total} limit={data.limit} onChange={setPage} />}
      </div>
    </>
  );
}
